import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { OrderDetail } from '@/components/orders/OrderDetail'
import { useToast } from '@/hooks/use-toast'
import apiClient from '@/lib/apiClient'

type OrderStatus = 'pending' | 'review' | 'processing' | 'shipped' | 'delivered' | 'cancelled'

const OrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const updateStatusMutation = useMutation({
    mutationFn: async ({ orderId, status }: { orderId: string; status: OrderStatus }) => {
      return apiClient.updateOrderStatus(orderId, {
        status,
        notify_customer: true,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['orderStats'] })
      queryClient.invalidateQueries({ queryKey: ['order', id] })
      toast({
        title: 'Success',
        description: 'Order status updated successfully',
      })
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to update order status. Please try again.',
        variant: 'destructive',
      })
    },
  })

  const updateTrackingMutation = useMutation({
    mutationFn: async ({
      orderId,
      trackingNumber,
      carrier,
    }: {
      orderId: string
      trackingNumber: string
      carrier: string
    }) => {
      // Get current order to preserve status
      const currentOrder = await apiClient.getOrder(orderId)
      const currentStatus = currentOrder.data.status

      return apiClient.updateOrderStatus(orderId, {
        status: currentStatus === 'shipped' || currentStatus === 'delivered' ? currentStatus : 'shipped',
        tracking_number: trackingNumber,
        carrier: carrier,
        notify_customer: true,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['orderStats'] })
      queryClient.invalidateQueries({ queryKey: ['order', id] })
      toast({
        title: 'Success',
        description: 'Tracking number updated successfully',
      })
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to update tracking number. Please try again.',
        variant: 'destructive',
      })
    },
  })

  const updateNoteMutation = useMutation({
    mutationFn: async ({ orderId, note }: { orderId: string; note: string }) => {
      return apiClient.updateOrder(orderId, {
        notes: note,
        append_notes: true,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['orderStats'] })
      queryClient.invalidateQueries({ queryKey: ['order', id] })
      toast({
        title: 'Success',
        description: 'Note added to order successfully',
      })
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to add note. Please try again.',
        variant: 'destructive',
      })
    },
  })

  const handleBack = () => {
    navigate('/orders')
  }

  const handleUpdateStatus = async (orderId: string, status: OrderStatus) => {
    updateStatusMutation.mutate({ orderId, status })
  }

  const handleUpdateTracking = async (orderId: string, trackingNumber: string, carrier: string) => {
    updateTrackingMutation.mutate({ orderId, trackingNumber, carrier })
  }

  const handleAddNote = async (orderId: string, note: string) => {
    updateNoteMutation.mutate({ orderId, note })
  }

  if (!id) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-destructive">Order ID is required</div>
      </div>
    )
  }

  return (
    <OrderDetail
      orderId={id}
      onBack={handleBack}
      onUpdateStatus={handleUpdateStatus}
      onUpdateTracking={handleUpdateTracking}
      onAddNote={handleAddNote}
    />
  )
}

export default OrderDetailPage

